import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { isDefined } from '@trimble-gcs/common';
import { TranslationService } from '@trimble-gcs/i18n';
import { map, Observable, of, switchMap } from 'rxjs';
import { AppState } from '../../app-state/app.state';
import { SetActiveBaseLayer, SetBaseLayers } from './base-layer.actions';
import { BaseLayer } from './base-layer.models';
import { BaseLayerState } from './base-layer.state';

@Injectable({
  providedIn: 'root',
})
export class BaseLayerService {
  private readonly store = inject(Store);
  private readonly translationService = inject(TranslationService);

  getBaseLayers() {
    return this.store.select(BaseLayerState.baseLayers).pipe(
      switchMap((layers) => {
        return isDefined(layers) ? of(layers) : this.createAndCacheBaseLayers();
      }),
    );
  }

  private createAndCacheBaseLayers() {
    return this.createBaseLayers().pipe(
      switchMap((layers) => {
        const activeLayer = this.store.selectSnapshot(BaseLayerState.activeBaseLayer);
        const newActiveLayer = layers.find((l) => l.name === activeLayer?.name) ?? layers[0];

        return this.store.dispatch(new SetActiveBaseLayer(newActiveLayer)).pipe(map(() => layers));
      }),
      switchMap((layers) => {
        return this.store.dispatch(new SetBaseLayers(layers)).pipe(map(() => layers));
      }),
    );
  }

  private createBaseLayers(): Observable<BaseLayer[]> {
    return this.store.selectOnce(AppState.settings).pipe(
      map(({ map: mapSettings }) => {
        const headers = { ['Authorization']: mapSettings.key };
        const language = this.translationService.currentLocale;
        const layers: BaseLayer[] = [
          {
            name: 'Satellite',
            templateUrl: mapSettings.url
              .replace('{style}', 'satellite')
              .replace('{language}', language),
            headers,
          },
          {
            name: 'Street',
            templateUrl: mapSettings.url
              .replace('{style}', 'default')
              .replace('{language}', language),
            headers,
          },
        ];

        return layers;
      }),
    );
  }
}
